import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { LeagueService, SeasonService, PredictionService, TeamService, ContestantService } from '../../services/firebaseService';
import { isMultiEliminationWeek } from '../../utils/predictionUtils';

const EmailRecap = () => {
  const { leagueId, seasonId, weekNumber } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [league, setLeague] = useState(null);
  const [season, setSeason] = useState(null);
  const [week, setWeek] = useState(null);
  const [teams, setTeams] = useState([]);
  const [predictions, setPredictions] = useState([]);
  const [contestants, setContestants] = useState([]);

  useEffect(() => {
    loadData();
  }, [leagueId, seasonId, weekNumber]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load league data
      const leagueData = await LeagueService.getLeague(leagueId);
      setLeague(leagueData);
      
      // Load season data
      const seasonData = await SeasonService.getSeason(seasonId);
      setSeason(seasonData);
      
      // Find the specific week
      const weekData = seasonData.weeks?.find(w => w.weekNumber === parseInt(weekNumber));
      setWeek(weekData);
      
      // Load teams
      const teamsData = await TeamService.getLeagueSeasonTeams(leagueId, seasonId);
      setTeams(teamsData);
      
      // Load predictions for this week
      const predictionsData = await PredictionService.getPredictionsBySeasonAndWeek(seasonId, parseInt(weekNumber));
      setPredictions(predictionsData);
      
      // Load contestants
      const contestantsData = await ContestantService.getContestantsBySeason(seasonId);
      setContestants(contestantsData);
      
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getContestantName = (contestantId) => {
    const contestant = contestants.find(c => c.id === contestantId);
    return contestant ? contestant.name : contestantId;
  };

  const getTeamName = (teamId) => {
    const team = teams.find(t => t.id === teamId);
    return team ? team.name : teamId;
  };

  // Check if a contestant has been eliminated in any week up to the target week
  const isContestantEliminated = (contestantId, targetWeekNum) => {
    if (!season || !season.weeks || !contestantId) return false;
    
    // Get contestant name for comparison
    const contestantName = getContestantName(contestantId);
    
    // Check all weeks up to and including the target week
    for (const weekData of season.weeks) {
      if (weekData.weekNumber > targetWeekNum) break;
      if (!weekData.results || Object.keys(weekData.results).length === 0) continue;
      
      const results = weekData.results;
      
      // Check for finale eliminations
      if (weekData.isFinale) {
        if (results.votedOut) {
          const votedOutName = getContestantName(results.votedOut);
          if (votedOutName === contestantName) return true;
        }
        if (results.fireMakingLoser) {
          const fireMakingLoserName = getContestantName(results.fireMakingLoser);
          if (fireMakingLoserName === contestantName) return true;
        }
        // Winner is not eliminated, so we don't check that
      }
      // Check for double / triple elimination (ordered results)
      else if (isMultiEliminationWeek(weekData) && Array.isArray(results.votedOut)) {
        for (const result of results.votedOut) {
          const resultContestantId = result.contestantId || result.contestantName || result;
          const resultContestantName = getContestantName(resultContestantId);
          if (resultContestantName === contestantName) return true;
        }
      }
      // Check for single elimination
      else if (results.votedOut) {
        const votedOutName = getContestantName(results.votedOut);
        if (votedOutName === contestantName) return true;
      }
    }
    
    return false;
  };

  const getPredictionStats = () => {
    const stats = {};
    
    // Get team IDs for this league
    const leagueTeamIds = teams.map(team => team.id);
    
    if (!week) return stats;
    
    predictions.forEach((prediction, index) => {
      // Only include predictions from teams in this league
      if (leagueTeamIds.includes(prediction.teamId) && prediction.predictions) {
        const teamName = getTeamName(prediction.teamId);
        
        if (week.isFinale) {
          // Finale: votedOut, fireMakingLoser, winner
          ['votedOut', 'fireMakingLoser', 'winner'].forEach(key => {
            const contestantId = prediction.predictions[key];
            if (contestantId && contestantId !== 'n/a' && contestantId !== 'null' && contestantId !== 'undefined' && contestantId !== null) {
              const contestantName = getContestantName(contestantId);
              // Double-check that we have a valid name (not null/undefined)
              if (contestantName && contestantName !== null && contestantName !== 'null') {
                if (!stats[contestantId]) {
                  stats[contestantId] = {
                    name: contestantName,
                    teams: []
                  };
                }
                stats[contestantId].teams.push({
                  teamName,
                  predictionType: key
                });
              }
            }
          });
        } else if (isMultiEliminationWeek(week)) {
          const legacyKeys = ['votedOut1', 'votedOut2'];
          if (week.eliminationType === 'triple') legacyKeys.push('votedOut3');
          legacyKeys.forEach(key => {
            const contestantId = prediction.predictions[key];
            if (contestantId && contestantId !== 'n/a' && contestantId !== 'null' && contestantId !== 'undefined' && contestantId !== null) {
              const contestantName = getContestantName(contestantId);
              // Double-check that we have a valid name (not null/undefined)
              if (contestantName && contestantName !== null && contestantName !== 'null') {
                if (!stats[contestantId]) {
                  stats[contestantId] = {
                    name: contestantName,
                    teams: []
                  };
                }
                stats[contestantId].teams.push({
                  teamName,
                  predictionType: key
                });
              }
            }
          });
          
          // Handle votedOut as an array: { contestantId, position } slots and/or plain IDs.
          // Infer pick number from array index when position is missing (triple often only had
          // position on pick 1, so picks 2–3 were dropped).
          if (Array.isArray(prediction.predictions.votedOut)) {
            const maxPos = week.eliminationType === 'triple' ? 3 : 2;
            prediction.predictions.votedOut.forEach((item, index) => {
              if (!item) return;

              let contestantId;
              if (typeof item === 'string' || typeof item === 'number') {
                contestantId = String(item);
              } else if (typeof item === 'object') {
                contestantId =
                  item.contestantId || item.contestantName || null;
              } else {
                return;
              }

              let pos = null;
              if (typeof item === 'object' && item != null) {
                const p = item.position;
                if (Number.isFinite(p) && p >= 1 && p <= maxPos) {
                  pos = p;
                }
              }
              if (pos == null) {
                pos = index + 1;
              }
              if (pos < 1 || pos > maxPos) return;

              const predictionKey = `votedOut${pos}`;

              if (
                contestantId &&
                contestantId !== 'n/a' &&
                contestantId !== 'null' &&
                contestantId !== 'undefined'
              ) {
                const contestantName = getContestantName(contestantId);

                if (contestantName && contestantName !== 'null') {
                  if (!stats[contestantId]) {
                    stats[contestantId] = {
                      name: contestantName,
                      teams: [],
                    };
                  }
                  stats[contestantId].teams.push({
                    teamName,
                    predictionType: predictionKey,
                  });
                }
              }
            });
          } else if (prediction.predictions.votedOut && !Array.isArray(prediction.predictions.votedOut)) {
            // Handle single elimination format (votedOut as string)
            const contestantId = prediction.predictions.votedOut;
            if (contestantId && contestantId !== 'n/a' && contestantId !== 'null' && contestantId !== 'undefined' && contestantId !== null) {
              const contestantName = getContestantName(contestantId);
              // Double-check that we have a valid name (not null/undefined)
              if (contestantName && contestantName !== null && contestantName !== 'null') {
                if (!stats[contestantId]) {
                  stats[contestantId] = {
                    name: contestantName,
                    teams: []
                  };
                }
                stats[contestantId].teams.push({
                  teamName,
                  predictionType: 'votedOut'
                });
              }
            }
          }
        } else {
          // Single elimination: votedOut
          const contestantId = prediction.predictions.votedOut;
          if (contestantId && contestantId !== 'n/a' && contestantId !== 'null' && contestantId !== 'undefined' && contestantId !== null) {
            const contestantName = getContestantName(contestantId);
            // Double-check that we have a valid name (not null/undefined)
            if (contestantName && contestantName !== null && contestantName !== 'null') {
              if (!stats[contestantId]) {
                stats[contestantId] = {
                  name: contestantName,
                  teams: []
                };
              }
              stats[contestantId].teams.push({
                teamName,
                predictionType: 'votedOut'
              });
            }
          }
        }
      }
    });
    
    
    return stats;
  };

  const getNonPredictingTeams = () => {
    // Consider a team as having made a prediction if any prediction value is truthy and not 'n/a'
    const leagueTeamIds = new Set(teams.map(team => team.id));

    const teamIdToMadePrediction = new Map();
    teams.forEach(team => {
      teamIdToMadePrediction.set(team.id, false);
    });

    if (!week) return [];

    predictions.forEach(prediction => {
      if (!leagueTeamIds.has(prediction.teamId)) return;
      const predObj = prediction.predictions || {};
      
      let made = false;
      if (week.isFinale) {
        // Finale: check votedOut, fireMakingLoser, winner
        made = ['votedOut', 'fireMakingLoser', 'winner'].some(key => 
          predObj[key] && predObj[key] !== 'n/a'
        );
      } else if (isMultiEliminationWeek(week)) {
        const legacyKeys = ['votedOut1', 'votedOut2'];
        if (week.eliminationType === 'triple') legacyKeys.push('votedOut3');
        made = legacyKeys.some(key =>
          predObj[key] && predObj[key] !== 'n/a' && predObj[key] !== 'null' && predObj[key] !== 'undefined'
        );
        
        // Also check if votedOut is an array with valid predictions
        if (!made && Array.isArray(predObj.votedOut)) {
          made = predObj.votedOut.some(item => {
            if (!item) return false;
            const contestantId = item.contestantId || item.contestantName || item;
            return contestantId && 
                   contestantId !== 'n/a' && 
                   contestantId !== 'null' && 
                   contestantId !== 'undefined' &&
                   contestantId !== null;
          });
        }
        
        // Also check single elimination format
        if (!made && predObj.votedOut && !Array.isArray(predObj.votedOut)) {
          made = predObj.votedOut && predObj.votedOut !== 'n/a' && predObj.votedOut !== 'null' && predObj.votedOut !== 'undefined';
        }
      } else {
        // Single elimination: check votedOut
        made = predObj.votedOut && predObj.votedOut !== 'n/a' && predObj.votedOut !== 'null' && predObj.votedOut !== 'undefined';
      }
      
      if (made) {
        teamIdToMadePrediction.set(prediction.teamId, true);
      }
    });

    const nonPredictors = [];
    teamIdToMadePrediction.forEach((made, teamId) => {
      if (!made) {
        nonPredictors.push(getTeamName(teamId));
      }
    });

    // Sort alphabetically for readability
    nonPredictors.sort((a, b) => a.localeCompare(b));
    return nonPredictors;
  };

  const getCurrentStandings = () => {
    const targetWeekNum = parseInt(weekNumber);
    
    // Calculate scores for each team up to and including the target week
    const teamScores = teams.map(team => {
      let score = 0;
      
      // Sum up weekly scores from week 1 to target week
      if (team.weeklyScores) {
        for (let weekNum = 1; weekNum <= targetWeekNum; weekNum++) {
          // weeklyScores keys are stored as strings (e.g., '1', '2', '3')
          const weekScore = team.weeklyScores[weekNum.toString()] || team.weeklyScores[weekNum] || 0;
          score += weekScore;
        }
      } else {
        // Fallback: if no weeklyScores, try to use currentScore as last resort
        // But this shouldn't happen if scores are calculated properly
        score = team.currentScore || 0;
      }
      
      // Get drafted contestant (first draft pick) - only if not eliminated
      let draftedContestant = '';
      if (team.draftPicks && Array.isArray(team.draftPicks) && team.draftPicks.length > 0) {
        const firstPick = team.draftPicks[0];
        const contestantId = firstPick.contestantId || firstPick.contestant?.contestantId || firstPick;
        if (contestantId) {
          // Only include contestant name if they haven't been eliminated
          if (!isContestantEliminated(contestantId, targetWeekNum)) {
            draftedContestant = getContestantName(contestantId);
          }
        }
      }
      
      return {
        id: team.id,
        name: team.name,
        score: score,
        draftedContestant: draftedContestant
      };
    });
    
    // Sort teams by score (descending)
    const sortedTeams = teamScores.sort((a, b) => b.score - a.score);
    
    // Calculate positions (teams with same score get same position)
    let currentPosition = 1;
    const teamsWithPositions = sortedTeams.map((team, index) => {
      // If this team has a different score than the previous team, update position
      if (index > 0 && sortedTeams[index - 1].score !== team.score) {
        currentPosition = index + 1;
      }
      return {
        ...team,
        position: currentPosition
      };
    });
    
    return teamsWithPositions;
  };

  const formatEmailRecap = () => {
    if (!week) {
      return `SURVIVOR FANTASY LEAGUE - WEEK ${weekNumber} RECAP\nLeague: ${league?.name}\nSeason: ${season?.name}\n\nWeek data not available.\n`;
    }
    
    const predictionStats = getPredictionStats();
    const standings = getCurrentStandings();
    
    let recap = `SURVIVOR FANTASY LEAGUE - WEEK ${weekNumber} RECAP\n`;
    recap += `League: ${league?.name}\n`;
    recap += `Season: ${season?.name}\n`;
    recap += `Week: ${week?.airDate ? new Date(week.airDate.seconds * 1000).toLocaleDateString() : 'TBD'}\n\n`;
    
    // Picks for the week
    recap += `PICKS FOR THE WEEK:\n`;
    recap += `==================\n\n`;
    
    if (Object.keys(predictionStats).length === 0) {
      recap += `No predictions were made for this week.\n\n`;
    } else {
      if (isMultiEliminationWeek(week)) {
        const maxPick = week.eliminationType === 'triple' ? 3 : 2;
        const pickStats = {};
        for (let p = 1; p <= maxPick; p++) {
          pickStats[p] = {};
        }

        Object.entries(predictionStats).forEach(([contestantId, data]) => {
          data.teams.forEach(team => {
            const m = team.predictionType?.match(/^votedOut([123])$/);
            if (!m) return;
            const num = parseInt(m[1], 10);
            if (num < 1 || num > maxPick) return;
            if (!pickStats[num][contestantId]) {
              pickStats[num][contestantId] = {
                name: data.name,
                teams: [],
              };
            }
            pickStats[num][contestantId].teams.push(team.teamName);
          });
        });

        for (let p = 1; p <= maxPick; p++) {
          const statsForPick = pickStats[p];
          if (Object.keys(statsForPick).length > 0) {
            recap += `PICK ${p} PREDICTIONS:\n`;
            Object.entries(statsForPick)
              .sort(([, a], [, b]) => b.teams.length - a.teams.length)
              .forEach(([, pdata]) => {
                const teamNames = pdata.teams.join(', ');
                recap += `${pdata.name} (${pdata.teams.length}): ${teamNames}\n`;
              });
          }
        }
      } else if (week.isFinale) {
        // For finale, organize by prediction type: votedOut, fireMakingLoser, winner
        const votedOutStats = {};
        const fireMakingLoserStats = {};
        const winnerStats = {};
        
        Object.entries(predictionStats).forEach(([contestantId, data]) => {
          data.teams.forEach(team => {
            if (team.predictionType === 'votedOut') {
              if (!votedOutStats[contestantId]) {
                votedOutStats[contestantId] = {
                  name: data.name,
                  teams: []
                };
              }
              votedOutStats[contestantId].teams.push(team.teamName);
            } else if (team.predictionType === 'fireMakingLoser') {
              if (!fireMakingLoserStats[contestantId]) {
                fireMakingLoserStats[contestantId] = {
                  name: data.name,
                  teams: []
                };
              }
              fireMakingLoserStats[contestantId].teams.push(team.teamName);
            } else if (team.predictionType === 'winner') {
              if (!winnerStats[contestantId]) {
                winnerStats[contestantId] = {
                  name: data.name,
                  teams: []
                };
              }
              winnerStats[contestantId].teams.push(team.teamName);
            }
          });
        });
        
        // Display Voted Out predictions
        if (Object.keys(votedOutStats).length > 0) {
          recap += `VOTED OUT:\n`;
          Object.entries(votedOutStats)
            .sort(([,a], [,b]) => b.teams.length - a.teams.length)
            .forEach(([contestantId, data]) => {
              const teamNames = data.teams.join(', ');
              recap += `${data.name} (${data.teams.length}): ${teamNames}\n`;
            });
        }
        
        // Display Fire-Making Loser predictions
        if (Object.keys(fireMakingLoserStats).length > 0) {
          recap += `FIRE-MAKING LOSER:\n`;
          Object.entries(fireMakingLoserStats)
            .sort(([,a], [,b]) => b.teams.length - a.teams.length)
            .forEach(([contestantId, data]) => {
              const teamNames = data.teams.join(', ');
              recap += `${data.name} (${data.teams.length}): ${teamNames}\n`;
            });
        }
        
        // Display Winner predictions
        if (Object.keys(winnerStats).length > 0) {
          recap += `WINNER:\n`;
          Object.entries(winnerStats)
            .sort(([,a], [,b]) => b.teams.length - a.teams.length)
            .forEach(([contestantId, data]) => {
              const teamNames = data.teams.join(', ');
              recap += `${data.name} (${data.teams.length}): ${teamNames}\n`;
            });
        }
      } else {
        // For single elimination, use the original format
        Object.entries(predictionStats)
          .sort(([,a], [,b]) => b.teams.length - a.teams.length)
          .forEach(([contestantId, data]) => {
            const teamNames = data.teams.map(team => team.teamName).join(', ');
            recap += `${data.name} (${data.teams.length}): ${teamNames}\n`;
          });
      }

      const nonPredictors = getNonPredictingTeams();
      if (nonPredictors.length > 0) {
        recap += `Didn't make a prediction (${nonPredictors.length}): ${nonPredictors.join(', ')}\n`;
      }
    }
    
    // Standings through this week
    recap += `\nSTANDINGS THROUGH WEEK ${weekNumber}:\n`;
    recap += `=====================================\n\n`;
    
    standings.forEach(({ name, score, position, draftedContestant }, index) => {
      // Only show "points" for the first team
      const pointsText = index === 0 ? 'points' : '';
      const contestantText = draftedContestant ? `(${draftedContestant})` : '';
      
      // Build the line
      let line = `${position}. ${name} - ${score}`;
      if (pointsText) {
        line += ` ${pointsText}`;
      }
      if (contestantText) {
        line += ` ${contestantText}`;
      }
      recap += `${line}\n`;
    });
    
    return recap;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-survivor-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate(`/manage-leagues/${leagueId}/season/${seasonId}/week/${weekNumber}`)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-survivor-600 hover:bg-survivor-700"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Back to Week Summary
          </button>
        </div>
      </div>
    );
  }

  // Only generate email recap if we have the necessary data
  const emailRecap = (week && season && teams.length > 0) ? formatEmailRecap() : 'Loading...';

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto py-6 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="px-4 py-6 sm:px-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={() => navigate(`/manage-leagues/${leagueId}/season/${seasonId}/week/${weekNumber}`)}
                className="mr-4 p-2 text-gray-400 hover:text-gray-600"
              >
                <ArrowLeftIcon className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  Email Recap - Week {weekNumber}
                </h1>
                <p className="mt-1 text-sm text-gray-500">
                  {league?.name} • {season?.name}
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                navigator.clipboard.writeText(emailRecap);
                alert('Email recap copied to clipboard!');
              }}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-survivor-600 hover:bg-survivor-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-survivor-500"
            >
              Copy to Clipboard
            </button>
          </div>
        </div>

        {/* Email Content */}
        <div className="px-4 py-6 sm:px-0">
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">Email Recap Content</h2>
              <p className="mt-1 text-sm text-gray-500">
                Copy the text below and paste it into your email
              </p>
            </div>
            <div className="px-6 py-4">
              <pre className="whitespace-pre-wrap text-sm text-gray-900 font-mono bg-gray-50 p-4 rounded border">
                {typeof emailRecap === 'string'
                  ? emailRecap.split('\n').map((line, i) => {
                      const colonIndex = line.indexOf(':');
                      if (colonIndex >= 0) {
                        return (
                          <React.Fragment key={i}>
                            <strong>{line.slice(0, colonIndex + 1)}</strong>
                            {line.slice(colonIndex + 1)}
                            {'\n'}
                          </React.Fragment>
                        );
                      }
                      return (
                        <React.Fragment key={i}>
                          {line}
                          {'\n'}
                        </React.Fragment>
                      );
                    })
                  : emailRecap}
              </pre>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailRecap;
